import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { faBox, faPen, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWarehouseMaterialAssignedLocations } from '../../hooks/useWarehouseMaterialAssignedLocations';
import { PackagingItem } from '../../models/Packaging';
import { AddStockToLocationModal, AddStockToLocationModalRef } from './AddStockToLocationModal';
import { AdjustStockModal, AdjustStockModalRef } from './AdjustStockModal';

export interface LocationsStockModalRef {
  open: (item: PackagingItem) => void;
}

interface LocationsStockModalProps {
  onSuccess?: () => void;
}

export const LocationsStockModal = forwardRef<LocationsStockModalRef, LocationsStockModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('materialAlmacenModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const adjustStockModalRef = useRef<AdjustStockModalRef>(null);
    const addStockModalRef = useRef<AddStockToLocationModalRef>(null);

    const [item, setItem] = useState<PackagingItem | null>(null);
    const productId = item?.id ?? null;
    const isOpen = item != null;

    const {
      data: locations = [],
      isLoading,
      error,
      refetch,
    } = useWarehouseMaterialAssignedLocations(productId, isOpen);

    const location = useMemo(() => locations[0] ?? null, [locations]);

    useImperativeHandle(ref, () => ({
      open: (packagingItem) => {
        setItem(packagingItem);
        modalRef.current?.openModal();
      },
    }));

    const handleRefresh = () => {
      refetch();
      onSuccess?.();
    };

    const handleAdjustStock = () => {
      if (!productId || !location) return;

      adjustStockModalRef.current?.open(
        {
          productId,
          locationId: location.id,
          locationCode: location.code,
          currentQty: location.stock ?? 0,
        },
        handleRefresh,
      );
    };

    const handleAssignLocation = () => {
      if (!productId || location) return;
      addStockModalRef.current?.open(productId, handleRefresh);
    };

    return (
      <>
        <ModalCenter
          ref={modalRef}
          title={t('modal.locations_stock.title')}
          icon={<FontAwesomeIcon icon={faBox} className="w-3 h-3" />}
          type="default"
          width="lg"
          className="h-[100dvh] sm:h-auto w-full sm:w-[680px]"
          onCloseModal={() => setItem(null)}
        >
          <div className="space-y-4 pt-3 pb-2">
            {item && (
              <div className="pb-3 border-b border-card-border">
                <Product
                  id={item.id}
                  src={item.image_url || ''}
                  name={item.name}
                  code={item.code}
                  acronym={item.name?.charAt(0).toUpperCase()}
                  enableLineClamp={false}
                  showImageModal={false}
                />
              </div>
            )}

            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-subtext">{t('modal.locations_stock.loading')}</p>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-destructive">{t('errors.generic')}</p>
              </div>
            )}

            {!isLoading && !error && location && (
              <div className="space-y-3">
                <p className="text-xs text-subtext">{t('modal.locations_stock.single_location_note')}</p>

                <div className="w-full rounded-md overflow-hidden border border-card-border">
                  <div className="grid grid-cols-12 text-sm border-b border-card-border py-2 px-3 bg-card-bg">
                    <div className="col-span-6 font-medium">{t('modal.locations_stock.location_code')}</div>
                    <div className="col-span-3 font-medium">{t('modal.locations_stock.location_stock')}</div>
                    <div className="col-span-3 font-medium text-right">{t('modal.locations_stock.adjust')}</div>
                  </div>

                  <div className="grid grid-cols-12 border-b border-card-border py-3 px-3 last:border-b-0 items-center">
                    <div className="col-span-6 text-sm font-medium truncate">{location.code}</div>
                    <div className="col-span-3 font-medium">{location.stock ?? 0}</div>
                    <div className="col-span-3 flex justify-end">
                      <button
                        type="button"
                        className="p-1 text-icons hover:text-title transition-colors"
                        title={t('modal.locations_stock.adjust')}
                        onClick={handleAdjustStock}
                        data-testid={`adjust-stock-${location.id}`}
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isLoading && !error && !location && (
              <>
                <div className="flex justify-end mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAssignLocation}
                    data-testid="assign-location-button"
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2 w-3.5" />
                    {t('modal.locations_stock.assign_location')}
                  </Button>
                </div>
                <div className="flex flex-col items-center justify-center text-center py-12">
                  <h3 className="text-lg font-medium text-title mb-2">{t('modal.locations_stock.empty_title')}</h3>
                  <p className="text-subtext max-w-xs">{t('modal.locations_stock.empty_description')}</p>
                </div>
              </>
            )}
          </div>
        </ModalCenter>

        <AdjustStockModal ref={adjustStockModalRef} />
        <AddStockToLocationModal ref={addStockModalRef} />
      </>
    );
  },
);

LocationsStockModal.displayName = 'LocationsStockModal';
